import CheckIcon from "../icons/CheckIcon";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { useState } from "react";

export default function PricingCard({
  planName,
  price,
  description,
  features,
  primary,
  isAnnual,
  planId,
  priceId,
  lead,
}: {
  planName: string;
  price: number;
  description: string;
  features: string[];
  primary: boolean;
  isAnnual?: boolean;
  planId: string;
  priceId: string;
  lead: string;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  // Calculate original annual price (without discount)
  const originalAnnualPrice = isAnnual ? Math.round(price / 0.7) : 0;

  const handleCheckout = async () => {
    if (!user) {
      // If user is not logged in, redirect to login page
      navigate("/login?redirect=pricing");
      return;
    }

    try {
      setIsLoading(true);
      // Call the backend to create a checkout session using api service
      const response = await api.post("/api/payment/create-checkout-session", {
        plan_id: priceId,
        is_annual: isAnnual || false,
        lead: lead,
      });

      const { url } = response.data;

      // Redirect to checkout page with the client secret
      // navigate(`/checkout?client_secret=${client_secret}`);
      window.location.href = url;
    } catch (error) {
      console.error("Error creating checkout session:", error);
      alert("There was a problem processing your payment. Please try again.");
      setIsLoading(false);
    }
  };

  const handleFreeVideos = async () => {
    if (!user) {
      // If user is not logged in, redirect to login page
      navigate("/login?redirect=pricing");
      return;
    }

    try {
      setIsLoading(true);
      // Call the backend to create a checkout session using api service
      const response = await api.post("/api/payment/create-checkout-session", {
        plan_id: priceId,
        is_annual: isAnnual || false,
        lead: true,
      });

      const { url } = response.data;
      window.location.href = url;
    } catch (error) {
      console.error("Error creating checkout session:", error);
      alert("There was a problem processing your payment. Please try again.");
      setIsLoading(false);
    }
  };

  const LoadingSpinner = ({ color = "text-white" }: { color?: string }) => (
    <svg
      className={`animate-spin h-5 w-5 ${color}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );

  function getItemText() {
    if (isLoading) {
      return "Processing... ";
    }
    if (lead === "true") {
      return "Get 10 Free Videos";
    }
    return "Get Started";
  }

  return (
    <div
      key={planName}
      className={`relative bg-white dark:bg-gray-800 rounded-2xl p-6 md:p-8 shadow-md hover:shadow-lg transition-all duration-200 ${
        primary === true ? "border border-blue-500 dark:border-blue-400" : "border border-gray-200 dark:border-gray-700"
      }`}
    >
      {primary ? (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-500 dark:bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-medium transition-colors duration-200">
          Most Popular
        </div>
      ) : (
        <></>
      )}
      {/* <div className="mb-4 bg-blue-50 text-blue-700 px-2.5 py-1 rounded-md text-xs font-medium inline-block">
        60-90 second videos
      </div> */}
      <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2 transition-colors duration-200">{planName}</h3>
      <div className="flex items-baseline mb-5">
        {isAnnual && (
          <span className="text-lg text-gray-400 dark:text-gray-500 line-through mr-2 transition-colors duration-200">
            ${Math.round(originalAnnualPrice / 12)}
          </span>
        )}
        <span className="text-3xl font-bold text-slate-800 dark:text-white transition-colors duration-200">
          ${isAnnual ? Math.round(price / 12) : price}
        </span>
        <span className="text-slate-500 dark:text-gray-400 ml-1 transition-colors duration-200">
          {isAnnual ? "/month" : "/month"}
        </span>
        {isAnnual && (
          <span className="ml-2 text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300 px-2 py-0.5 rounded-full transition-colors duration-200">
            30% off
          </span>
        )}
      </div>

      {primary === true ? (
        <button
          onClick={handleCheckout}
          disabled={isLoading}
          className="w-full py-3 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 dark:from-blue-600 dark:to-indigo-600 dark:hover:from-blue-700 dark:hover:to-indigo-700 text-white font-medium rounded-xl flex items-center justify-center gap-2 transition-all duration-200 shadow-md disabled:opacity-80 hover:cursor-pointer"
        >
          {isLoading ? <LoadingSpinner /> : null}
          <span>{getItemText()}</span>
          {/* <span>{isLoading ? "Processing... " : "Get 10 Free Videos"}</span> */}
        </button>
      ) : (
        <button
          onClick={handleCheckout}
          disabled={isLoading}
          className="w-full py-3 bg-white dark:bg-gray-800 border border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400 font-medium rounded-xl flex items-center justify-center gap-2 hover:bg-blue-50 dark:hover:bg-gray-700 transition-all duration-200 disabled:opacity-80 hover:cursor-pointer"
        >
          {isLoading ? <LoadingSpinner color="text-gray-700 dark:text-gray-300" /> : null}
          <span>{getItemText()}</span>
        </button>
      )}

      <div className="text-center mt-1.5">
        <button
          onClick={handleFreeVideos}
          className="py-3 px-8 w-full text-sm border border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400 font-medium rounded-lg hover:bg-blue-50 dark:hover:bg-gray-700 transition-all duration-200 cursor-pointer"
        >
          Get 10 Free Videos
        </button>
      </div>

      {/* <p className="text-lg text-slate-600 mb-6 pb-6 border-b border-gray-100">
        {description}
      </p> */}

      <p className="text-base text-slate-600 dark:text-gray-300 mt-4 font-medium transition-colors duration-200">Includes</p>
      <ul className="space-y-3 mb-8 mt-2 pt-6 border-t border-gray-200 dark:border-gray-700 transition-colors duration-200">
        {features.map((feature) => (
          <li
            className="flex items-center text-base text-slate-700 dark:text-gray-300 transition-colors duration-200"
            key={feature}
          >
            <CheckIcon />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
